import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { fromDateTimeLocalValue } from '../../shared/clientTime';

/**
 * Rejestr zmiany zlecenia „było → ma być” — klient API
 * (`apps/api/src/order-changes/`, makieta back office v2, fala B, B9;
 * przełącznik `orderChangeRegister`, S-21.10-09). Do TMS nic nie zapisujemy:
 * po decyzji przyjmujący zlecenia wprowadza zmianę w TMS ręcznie i oznacza
 * „Wprowadzone w TMS”, a zamyka ją import z TMS.
 */

/** Klucz przełącznika (rejestr `feature-flags.ts` w API). */
export const FEATURE_ORDER_CHANGE_REGISTER = 'orderChangeRegister';

/** Zdarzenie okna „zmiany się zmieniły” — licznik w menu odświeża się od razu. */
export const ORDER_CHANGES_CHANGED_EVENT = 'order-changes-changed';
export const notifyOrderChangesChanged = (): void => {
  window.dispatchEvent(new Event(ORDER_CHANGES_CHANGED_EVENT));
};

/** Pola zmiany — kody z API (dane), napisy jak w API. */
export const ORDER_CHANGE_FIELDS: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'loadingWindow', name: 'Okno załadunku' },
  { id: 'unloadingWindow', name: 'Okno rozładunku' },
  { id: 'loadingPlace', name: 'Miejsce załadunku' },
  { id: 'unloadingPlace', name: 'Miejsce rozładunku' },
  { id: 'weight', name: 'Waga' },
  { id: 'ldm', name: 'LDM' },
  { id: 'price', name: 'Stawka' },
  { id: 'notes', name: 'Uwagi i inne zmiany' },
];

export const ORDER_CHANGE_ORIGINS: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'client', name: 'Na życzenie klienta' },
  { id: 'execution', name: 'Na wniosek osoby realizującej (np. brak auta na termin)' },
];

/** Filtr stanu listy — wartości jak w API (`orderChangeStatusFilter`). */
export const ORDER_CHANGE_STATUS_FILTERS: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'open', name: 'W toku' },
  { id: 'pending', name: 'Czekają na decyzję' },
  { id: 'accepted', name: 'Do wprowadzenia w TMS' },
  { id: 'entered', name: 'Wprowadzone, czekają na import' },
  { id: 'closed', name: 'Zamknięte' },
  { id: 'rejected', name: 'Odrzucone' },
  { id: 'withdrawn', name: 'Wycofane' },
  { id: 'all', name: 'Wszystkie' },
];

export interface OrderChangeApproval {
  by: string | null;
  at: string;
  comment: string | null;
}

export interface OrderChange {
  id: number;
  userOfferId: number;
  orderNumber: string | null;
  field: string;
  fieldLabel: string;
  previousValue: string | null;
  requestedValue: string;
  origin: string;
  originLabel: string;
  reason: string;
  status: string;
  statusLabel: string;
  requestedByName: string | null;
  requestedAt: string;
  csApproval: OrderChangeApproval | null;
  executionApproval: OrderChangeApproval | null;
  rejection: { by: string | null; at: string; reason: string | null } | null;
  entered: OrderChangeApproval | null;
  closed: { at: string; by: string | null; note: string | null } | null;
  reminder: { at: string; text: string | null } | null;
  decisionRule: string | null;
  decisionRuleLabel: string | null;
  waitingFor: string[];
  comparedByImport: boolean;
  /**
   * Właściciel pola (21.10.2026, paczka p. Mariusza 21.10 BR-CS-02;
   * przełącznik `orderChangeFieldOwner`): kto akceptuje; `working` — wartość
   * robocza. Bez przełącznika API pól nie oddaje.
   */
  owner?: { side: 'cs' | 'administration'; label: string; working: boolean };
  administrationApproval?: OrderChangeApproval | null;
  /** Uwagi osoby realizującej (konsultacja, nie zgoda). */
  consultations?: Array<{ byId: number | null; byName: string | null; at: string; text: string }>;
  /**
   * Zgoda Team Leadera działu po wyjeździe na załadunek (S-25.10-03,
   * przełącznik `orderChangeLeadAfterDeparture`): na kogo czeka zmiana i skąd
   * wiemy, że auto wyruszyło. Bez przełącznika albo przed wyjazdem — brak pola.
   */
  leadApproval?: { leads: string[]; reason: string };
  can: { decide: boolean; enter: boolean; withdraw: boolean; consult?: boolean };
}

export interface OrderChangesForOrder {
  items: OrderChange[];
  canReport: boolean;
  reportRefusal: string | null;
}

export async function fetchOrderChanges(userOfferId: number | string): Promise<OrderChangesForOrder> {
  const { data } = await axiosNest.get<OrderChangesForOrder>(`/order-changes/order/${userOfferId}`);
  return { items: data?.items ?? [], canReport: !!data?.canReport, reportRefusal: data?.reportRefusal ?? null };
}

export async function fetchOrderChangeList(filter: {
  status?: string;
  reminder?: boolean;
}): Promise<OrderChange[]> {
  const { data } = await axiosNest.get<OrderChange[]>('/order-changes', {
    params: {
      ...(filter.status ? { status: filter.status } : {}),
      ...(filter.reminder ? { reminder: 'true' } : {}),
    },
  });
  return Array.isArray(data) ? data : [];
}

export async function fetchOrderChangeCount(): Promise<number> {
  const { data } = await axiosNest.get<{ count: number }>('/order-changes/count');
  return typeof data?.count === 'number' ? data.count : 0;
}

/** „Ma być” z formularza — pola zależne od rodzaju zmiany. */
export interface ReportInput {
  userOfferId: number;
  field: string;
  origin: string;
  reason: string;
  from?: string;
  to?: string;
  country?: string;
  postalCode?: string;
  city?: string;
  value?: string;
  amount?: string;
  currency?: string;
  text?: string;
}

/** Ciało zgłoszenia: terminy w czasie polskim idą z przesunięciem Warszawy. */
export function reportBody(input: ReportInput): Record<string, unknown> {
  const requested: Record<string, unknown> = {};
  switch (input.field) {
    case 'loadingWindow':
    case 'unloadingWindow':
      requested.from = fromDateTimeLocalValue(input.from ?? '');
      requested.to = fromDateTimeLocalValue(input.to ?? '');
      break;
    case 'loadingPlace':
    case 'unloadingPlace':
      requested.country = input.country;
      requested.postalCode = input.postalCode;
      requested.city = input.city;
      break;
    case 'weight':
    case 'ldm':
      requested.value = input.value;
      break;
    case 'price':
      requested.amount = input.amount;
      requested.currency = input.currency;
      break;
    case 'notes':
      requested.text = input.text;
      break;
  }
  return {
    userOfferId: input.userOfferId,
    field: input.field,
    origin: input.origin,
    reason: input.reason,
    requested,
  };
}

export async function reportOrderChange(input: ReportInput): Promise<OrderChange> {
  const { data } = await axiosNest.post<OrderChange>('/order-changes', reportBody(input));
  return data;
}

export async function decideOrderChange(
  id: number,
  decision: 'accept' | 'reject',
  comment: string,
): Promise<OrderChange> {
  const { data } = await axiosNest.post<OrderChange>(`/order-changes/${id}/decision`, { decision, comment });
  return data;
}

export async function markOrderChangeEntered(id: number, comment: string): Promise<OrderChange> {
  const { data } = await axiosNest.post<OrderChange>(`/order-changes/${id}/entered`, {
    comment: comment.trim() === '' ? undefined : comment,
  });
  return data;
}

/** Uwaga osoby realizującej (21.10.2026) — konsultacja, nie decyzja. */
export async function consultOrderChange(id: number, comment: string): Promise<OrderChange> {
  const { data } = await axiosNest.post<OrderChange>(`/order-changes/${id}/consultation`, { comment });
  return data;
}

export async function withdrawOrderChange(id: number, reason: string): Promise<OrderChange> {
  const { data } = await axiosNest.post<OrderChange>(`/order-changes/${id}/withdraw`, { reason });
  return data;
}

export { toErrorMessage };
